import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Autocomplete,
  TableSortLabel
} from '@mui/material';
import { readInventoryFile, readMultiplePlatforms, getAvailablePlatforms, getWarehousePlatform, isWarehousePlatform, standardizeInventoryData } from '../utils/inventoryDataReader';
import { analyzeInventory, filterAnalysisResults, exportToCSV } from '../utils/inventoryAnalysisEngine';
import { readMinMaxFile, createSystemLookupMap } from '../utils/minMaxDataReader';
import { loadHierarchyLookup, getDepartmentFromClass, getHierarchyFromClass } from '../utils/hierarchyLookup';

// Logo import
import ClarisLogo from '../assets/claris_logo_transparent[67291746].png';

// TODO: Scalability Note for Future Enhancement
// Current checkbox interface works for 11 platforms but will need redesign for 80+ platforms.
// Future improvements: searchable multi-select, regional groupings, favorites, etc.

const InventoryAnalysis = () => {
  const [selectedPlatforms, setSelectedPlatforms] = useState(getAvailablePlatforms());
  const [warehouseSelected, setWarehouseSelected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [summary, setSummary] = useState(null);
  const [smartOnSystemSummary, setSmartOnSystemSummary] = useState(null);
  const [isWarehouseAnalysis, setIsWarehouseAnalysis] = useState(false);
  const [filters, setFilters] = useState({
    status: [],
    class: [],
    productLine: [],
    department: [],
    platform: [],
    search: '',
    storeNumber: ''
  });
  const [filteredResults, setFilteredResults] = useState([]);
  const [sortConfig, setSortConfig] = useState({ orderBy: null, order: 'asc' });
  
  // Hierarchy lookup state
  const [hierarchyLookup, setHierarchyLookup] = useState(null);

  const platforms = getAvailablePlatforms();

  // Load hierarchy lookup on mount
  useEffect(() => {
    const loadHierarchy = async () => {
      const lookup = await loadHierarchyLookup();
      setHierarchyLookup(lookup);
    };
    loadHierarchy();
  }, []);

  useEffect(() => {
    console.log('=== INVENTORY ANALYSIS DATA FLOW TRACE ===');
    console.log('analysisResults exists:', !!analysisResults);
    console.log('analysisResults length:', analysisResults ? analysisResults.length : 0);
    console.log('Current filters:', filters);
    
    if (enhancedResults.length > 0) {
      console.log('Starting filtering process with enhanced results...');
      console.log('Enhanced results include departments:', enhancedResults[0]?.department);
      
      // FORCE CLEAR FIRST to ensure fresh state
      console.log('Clearing filteredResults first...');
      setFilteredResults([]);
      
      // Then set new filtered data after a brief delay
      setTimeout(() => {
        let filtered = enhancedResults;
        
        // Apply multi-select filters with OR logic within each filter and AND logic between filters
        
        // Status filter - OR logic (match any selected status)
        if (filters.status.length > 0) {
          filtered = filtered.filter(item => filters.status.includes(item.status));
          console.log('Applied status filter:', filters.status, '- Result:', filtered.length, 'items');
        }
        
        // Platform filter - OR logic (match any selected platform)
        if (filters.platform.length > 0) {
          filtered = filtered.filter(item => filters.platform.includes(item.analysisplatform));
          console.log('Applied platform filter:', filters.platform, '- Result:', filtered.length, 'items');
        }
        
        // Class filter - OR logic (match any selected class)
        if (filters.class.length > 0) {
          filtered = filtered.filter(item => filters.class.includes(item.class));
          console.log('Applied class filter:', filters.class, '- Result:', filtered.length, 'items');
        }
        
        // Product Line filter - OR logic (match any selected product line)
        if (filters.productLine.length > 0) {
          filtered = filtered.filter(item => filters.productLine.includes(item.productLine));
          console.log('Applied product line filter:', filters.productLine, '- Result:', filtered.length, 'items');
        }
        
        // Department filter - OR logic (match any selected department)
        if (filters.department.length > 0) {
          filtered = filtered.filter(item => filters.department.includes(item.department));
          console.log('Applied department filter:', filters.department, '- Result:', filtered.length, 'items');
        }
        
        // Search filter
        if (filters.search.trim()) {
          const searchLower = filters.search.toLowerCase();
          filtered = filtered.filter(item => 
            item.sku?.toString().toLowerCase().includes(searchLower) ||
            item.description?.toLowerCase().includes(searchLower)
          );
          console.log('Applied search filter:', filters.search, '- Result:', filtered.length, 'items');
        }
        
        // Store number filter
        if (filters.storeNumber.trim()) {
          filtered = filtered.filter(item => 
            item.site?.toString().includes(filters.storeNumber.trim())
          );
          console.log('Applied store number filter:', filters.storeNumber, '- Result:', filtered.length, 'items');
        }
        
        console.log('Filter function returned:', filtered.length, 'items');
        
        console.log('Setting NEW filteredResults state...');
        setFilteredResults(filtered);
        
        console.log('First 3 items in NEW state:');
        filtered.slice(0, 3).forEach((item, i) => {
          console.log(`NEW State Item ${i}: SKU ${item.sku}, Status "${item.status}", Department "${item.department}", Platform ${item.analysisplatform}`);
        });
      }, 10);
      
    } else {
      console.log('No analysisResults available, clearing filteredResults');
      setFilteredResults([]);
    }
    
    console.log('=== END DATA FLOW TRACE ===');
  }, [analysisResults, filters, hierarchyLookup]);

  const handlePlatformToggle = (platform) => {
    setSelectedPlatforms(prev => {
      if (prev.includes(platform)) {
        return prev.filter(p => p !== platform);
      } else {
        return [...prev, platform];
      }
    });
  };

  const handleSelectAll = () => {
    setSelectedPlatforms(platforms);
    setWarehouseSelected(false); // Can't mix warehouse with platforms
  };

  const handleDeselectAll = () => {
    setSelectedPlatforms([]);
    setWarehouseSelected(false);
  };

  const handleWarehouseToggle = () => {
    if (!warehouseSelected) {
      // Selecting warehouse - deselect all platforms
      setWarehouseSelected(true);
      setSelectedPlatforms([]);
    } else {
      // Deselecting warehouse
      setWarehouseSelected(false);
    }
  };

  const handlePlatformToggleWithWarehouseCheck = (platform) => {
    if (warehouseSelected) {
      // If warehouse is selected, deselect it first when selecting a platform
      setWarehouseSelected(false);
    }
    handlePlatformToggle(platform);
  };

  const handleRunAnalysis = async () => {
    const platformsToAnalyze = warehouseSelected ? ['W004'] : selectedPlatforms;

    if (platformsToAnalyze.length === 0) {
      alert('Please select at least one platform or the warehouse');
      return;
    }

    setLoading(true);
    setIsWarehouseAnalysis(warehouseSelected);

    try {
      console.log('Running analysis for:', warehouseSelected ? 'W004 Warehouse' : `platforms: ${platformsToAnalyze.join(', ')}`);

      const allData = await readMultiplePlatforms(platformsToAnalyze);
      if (allData.length === 0) {
        alert('No data found for selected platforms');
        setLoading(false);
        return;
      }

      let analysis;

      // WAREHOUSE ANALYSIS: Skip delivery schedules and system min/max
      if (warehouseSelected) {
        console.log('🏭 WAREHOUSE ANALYSIS: Skipping delivery schedules and system min/max');
        analysis = analyzeInventory(allData, {}, null);
      } else {
        // PLATFORM ANALYSIS: Load delivery schedules and system min/max
        console.log('=== LOADING DELIVERY SCHEDULES FROM DATABASE ===');

        let deliverySchedule = {};
        try {
          // Load from database API instead of localStorage
          const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';
          const response = await fetch(`${API_BASE}/api/delivery-schedules`);
          const schedules = await response.json();

          console.log('📅 Raw schedules from database:', schedules.length, 'platforms');
          console.log('Sample database entry:', schedules[0]);

          // Convert from database format to skip days format
          // Database: {platform: "ATL", schedule: [1,2,3,4,5]} (delivery days)
          // UI needs: {ATL: [6,7]} (skip days)
          schedules.forEach(item => {
            const allDays = [1, 2, 3, 4, 5, 6, 7];
            const skipDays = allDays.filter(d => !item.schedule.includes(d));
            deliverySchedule[item.platform] = skipDays;
          });

          console.log('✅ Delivery schedules loaded from database:', deliverySchedule);
          console.log('Available platforms:', Object.keys(deliverySchedule));
          console.log('ATL schedule (skip days):', deliverySchedule.ATL);
          console.log('ASH schedule (skip days):', deliverySchedule.ASH);

        } catch (error) {
          console.error('❌ Failed to load delivery schedules from database:', error);
          deliverySchedule = {};
        }
        console.log('Available platforms in schedule:', Object.keys(deliverySchedule));

        // Check SAV specifically
        if (deliverySchedule['SAV']) {
          console.log('✓ SAV delivery schedule found:', deliverySchedule['SAV']);
        } else {
          console.warn('✗ SAV delivery schedule MISSING');
          console.log('All available platforms:', Object.keys(deliverySchedule));
          // Add default schedule for SAV if missing
          deliverySchedule['SAV'] = ['Saturday', 'Sunday']; // Skip weekends (common pattern)
          console.log('Added default SAV schedule (skip weekends):', deliverySchedule['SAV']);
        }

        // BUG 2 FIX: Force cache invalidation to reload system data with leading zero fix
        localStorage.removeItem('systemMinMaxData');
        localStorage.removeItem('minMaxDataCache');
        console.warn('🔄 FORCE RELOAD: Cleared all system data cache - will reload fresh from file');

        // Load Min/Max data for system analysis
        console.log('🔄 About to load system Min/Max data...');
        const minMaxData = await readMinMaxFile();
        console.log('📦 System Min/Max file load completed, received Map with', minMaxData.size, 'entries');
        const systemLookupMap = minMaxData.size > 0 ? createSystemLookupMap(minMaxData) : null;

        console.log('📊 System lookup map created with', minMaxData.size, 'entries');

        // Debug min/max file loading
        console.log('=== MIN/MAX FILE LOADED ===');
        console.log('Total entries in lookup:', minMaxData.size);
        console.log('Sample keys:', Array.from(minMaxData.keys()).slice(0, 10));
        console.log('Sample values:', Array.from(minMaxData.entries()).slice(0, 3));

        // Test with known good values from database debug
        console.log('=== TESTING KNOWN GOOD VALUES ===');
        const testCases = [
          { store: "157", sku: "695", expectedKey: "157-695" },
          { store: "157", sku: "696", expectedKey: "157-696" },
          { store: "157", sku: "964", expectedKey: "157-964" }
        ];

        testCases.forEach(test => {
          const found = minMaxData.has(test.expectedKey);
          const data = minMaxData.get(test.expectedKey);

          console.log('🧪 Test Case:', {
            expectedKey: test.expectedKey,
            foundInLookup: found,
            data: data
          });
        });

        // VERIFICATION: Confirm system data was reloaded with leading zero fix
        if (systemLookupMap && systemLookupMap.size > 0) {
          console.log('=== SYSTEM DATA RELOAD VERIFICATION ===');
          const sampleKeys = Array.from(systemLookupMap.keys()).slice(0, 10);
          console.log('Sample system lookup keys (should NOT have leading zeros):', sampleKeys);

          // Look for SAV store keys specifically
          const savKeys = Array.from(systemLookupMap.keys()).filter(k =>
            k.includes('-222') || k.includes('-223') || k.includes('-224') || k.includes('-227')
          ).slice(0, 5);
          console.log('SAV store keys found in system data:', savKeys.length, 'keys');
          console.log('Sample SAV keys:', savKeys);

          if (savKeys.length === 0) {
            console.error('🚨 STILL NO SAV KEYS FOUND - leading zero fix may not be working');
          } else {
            console.log('✓ SAV keys found - leading zero fix appears to be working');
          }
        }

        // Debug what delivery schedule data we're passing to analysis
        console.log('=== DELIVERY SCHEDULE BEING PASSED TO ANALYSIS ===');
        console.log('deliverySchedule type:', typeof deliverySchedule);
        console.log('deliverySchedule keys:', Object.keys(deliverySchedule));
        console.log('Sample platform schedules (skip days from config):', {
          ATL: deliverySchedule.ATL,
          ASH: deliverySchedule.ASH,
          BNA: deliverySchedule.BNA
        });

        // Convert skip days format to delivery days format for analysis
        const analysisDeliverySchedules = {};
        Object.entries(deliverySchedule).forEach(([platform, skipDays]) => {
          // Convert skip days to delivery days (inverse logic)
          const allDays = [1, 2, 3, 4, 5, 6, 7]; // Monday=1, Sunday=7
          const deliveryDays = allDays.filter(day => !skipDays.includes(day));
          analysisDeliverySchedules[platform] = deliveryDays;
        });

        console.log('📅 Converted to delivery days format for analysis:', {
          ATL: analysisDeliverySchedules.ATL,
          ASH: analysisDeliverySchedules.ASH,
          BNA: analysisDeliverySchedules.BNA
        });

        analysis = analyzeInventory(allData, analysisDeliverySchedules, systemLookupMap);
      }
      
      // Debug analysis results
      console.log('=== ANALYSIS COMPLETE ===');
      console.log('Total analyzed items:', analysis.analyzedData.length);
      
      const itemsWithSystemData = analysis.analyzedData.filter(item => 
        item.systemMin !== null && item.systemMin !== undefined &&
        item.systemMax !== null && item.systemMax !== undefined
      );
      
      console.log('Items with system data:', itemsWithSystemData.length);
      console.log('Sample items with system data:', itemsWithSystemData.slice(0, 5).map(i => ({
        sku: i.sku,
        store: i.site,
        systemMin: i.systemMin,
        systemMax: i.systemMax,
        platform: i.analysisplatform
      })));
      
      // Platform match statistics
      console.log('=== PLATFORM MATCH RATES ===');
      const platformMatches = {};
      
      analysis.analyzedData.forEach(item => {
        const platform = item.analysisplatform || 'UNKNOWN';
        if (!platformMatches[platform]) {
          platformMatches[platform] = { total: 0, matched: 0 };
        }
        
        platformMatches[platform].total++;
        if (item.systemMin !== null && item.systemMax !== null) {
          platformMatches[platform].matched++;
        }
      });
      
      Object.entries(platformMatches).forEach(([platform, stats]) => {
        const rate = (stats.matched / stats.total * 100).toFixed(1);
        console.log(`${platform}: ${stats.matched}/${stats.total} (${rate}%)`);
      });
      
      setAnalysisResults(analysis.analyzedData);
      setSummary(analysis.summary);
      
      // Calculate Smart logic on System SKUs only for comparison
      const systemSkusOnly = analysis.analyzedData.filter(item => 
        item.systemMin !== null && item.systemMin !== undefined &&
        item.systemMax !== null && item.systemMax !== undefined
      );
      
      console.log('Smart-on-System calculation: filtered to', systemSkusOnly.length, 'SKUs with system data');
      
      // DEBUG: Calculate total inventory value using direct method for comparison
      const smartOnSystemDirectTotal = systemSkusOnly.reduce((sum, item) => sum + (item.onHandValue || 0), 0);
      
      const smartOnSystemStats = {
        skuCount: systemSkusOnly.length,
        outOfStock: systemSkusOnly.filter(item => item.status === 'Out of Stock').length,
        understocked: systemSkusOnly.filter(item => item.status === 'Understocked').length,
        correctlyStocked: systemSkusOnly.filter(item => item.status === 'Correctly Stocked').length,
        overstocked: systemSkusOnly.filter(item => item.status === 'Overstocked').length,
        outOfStockValue: systemSkusOnly.filter(item => item.status === 'Out of Stock').reduce((sum, item) => sum + (item.onHandValue || 0), 0),
        understockedValue: systemSkusOnly.filter(item => item.status === 'Understocked').reduce((sum, item) => sum + Math.abs(item.varianceValue || 0), 0),
        correctlyStockedValue: systemSkusOnly.filter(item => item.status === 'Correctly Stocked').reduce((sum, item) => sum + (item.onHandValue || 0), 0),
        overstockedValue: systemSkusOnly.filter(item => item.status === 'Overstocked').reduce((sum, item) => sum + (item.varianceValue || 0), 0),
        totalInventoryValue: smartOnSystemDirectTotal // Add direct total for comparison
      };
      
      // DEBUG: Show Smart-on-System calculation methods
      console.log('\n=== 🔍 SMART-ON-SYSTEM TOTAL VALUE CALCULATION ===');
      const smartComponentSum = (smartOnSystemStats.outOfStockValue || 0) + 
                               (smartOnSystemStats.understockedValue || 0) + 
                               (smartOnSystemStats.correctlyStockedValue || 0) + 
                               (smartOnSystemStats.overstockedValue || 0);
      console.log(`Smart Component Sum Method: $${Math.round(smartComponentSum).toLocaleString()}`);
      console.log(`Smart Direct Total Method: $${Math.round(smartOnSystemDirectTotal).toLocaleString()}`);
      console.log(`Smart Calculation Breakdown:`);
      console.log(`  - Out of Stock Value: $${Math.round(smartOnSystemStats.outOfStockValue || 0).toLocaleString()} (onHandValue for 0-stock items)`);
      console.log(`  - Understocked Value: $${Math.round(smartOnSystemStats.understockedValue || 0).toLocaleString()} (variance value - likely wrong)`);
      console.log(`  - Correctly Stocked Value: $${Math.round(smartOnSystemStats.correctlyStockedValue || 0).toLocaleString()} (onHandValue)`);
      console.log(`  - Overstocked Value: $${Math.round(smartOnSystemStats.overstockedValue || 0).toLocaleString()} (variance value - likely wrong)`);
      console.log(`Smart Total Difference: $${Math.round(smartOnSystemDirectTotal - smartComponentSum).toLocaleString()}`);
      console.log('=== END SMART-ON-SYSTEM DEBUG ===\n');
      
      // Calculate percentages
      smartOnSystemStats.outOfStockPercent = smartOnSystemStats.skuCount > 0 ? ((smartOnSystemStats.outOfStock / smartOnSystemStats.skuCount) * 100).toFixed(1) : '0';
      smartOnSystemStats.understockedPercent = smartOnSystemStats.skuCount > 0 ? ((smartOnSystemStats.understocked / smartOnSystemStats.skuCount) * 100).toFixed(1) : '0';
      smartOnSystemStats.correctlyStockedPercent = smartOnSystemStats.skuCount > 0 ? ((smartOnSystemStats.correctlyStocked / smartOnSystemStats.skuCount) * 100).toFixed(1) : '0';
      smartOnSystemStats.overstockedPercent = smartOnSystemStats.skuCount > 0 ? ((smartOnSystemStats.overstocked / smartOnSystemStats.skuCount) * 100).toFixed(1) : '0';
      
      console.log('Smart-on-System stats calculated:', smartOnSystemStats);
      setSmartOnSystemSummary(smartOnSystemStats);
      
      // Save to localStorage with timestamp
      const analysisRecord = {
        platforms: selectedPlatforms,
        timestamp: new Date().toISOString(),
        summary: analysis.summary,
        dataCount: analysis.analyzedData.length
      };
      
      const previousAnalyses = JSON.parse(localStorage.getItem('inventoryAnalyses') || '[]');
      previousAnalyses.unshift(analysisRecord);
      localStorage.setItem('inventoryAnalyses', JSON.stringify(previousAnalyses.slice(0, 10)));
      
    } catch (error) {
      console.error('Error running analysis:', error);
      alert('Error running analysis. Check console for details.');
    }
    setLoading(false);
  };

  const handleExport = () => {
    if (filteredResults.length === 0) {
      alert('No data to export');
      return;
    }
    exportToCSV(filteredResults, selectedPlatforms.join('-'));
  };

  const handleClearAndReload = () => {
    console.log('=== CLEARING AND RELOADING ===');
    setAnalysisResults(null);
    setSummary(null);
    setSmartOnSystemSummary(null);
    setFilteredResults([]);
    setFilters({
      status: [],
      class: [],
      productLine: [],
      department: [],
      platform: [],
      search: '',
      storeNumber: ''
    });
    console.log('All data cleared, states reset');
  };

  const handleClearAllFilters = () => {
    setFilters({
      status: [],
      class: [],
      productLine: [],
      department: [],
      platform: [],
      search: '',
      storeNumber: ''
    });
    setSortConfig({ orderBy: null, order: 'asc' }); // Reset sorting when clearing filters
  };

  // Sorting functionality
  const handleRequestSort = (property) => {
    const isAsc = sortConfig.orderBy === property && sortConfig.order === 'asc';
    const isDesc = sortConfig.orderBy === property && sortConfig.order === 'desc';
    
    if (!sortConfig.orderBy || sortConfig.orderBy !== property) {
      // First click on this column - sort ascending
      setSortConfig({ orderBy: property, order: 'asc' });
    } else if (isAsc) {
      // Second click - sort descending
      setSortConfig({ orderBy: property, order: 'desc' });
    } else if (isDesc) {
      // Third click - return to unsorted
      setSortConfig({ orderBy: null, order: 'asc' });
    }
  };

  const sortData = (data) => {
    if (!sortConfig.orderBy) {
      return data; // Return original order if no sort
    }

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.orderBy];
      let bValue = b[sortConfig.orderBy];

      // Handle special tier ordering
      if (sortConfig.orderBy === 'tier') {
        const tierOrder = { 'Top 10%': 1, 'Top 30%': 2, 'Bottom 70%': 3 };
        aValue = tierOrder[aValue] || 999;
        bValue = tierOrder[bValue] || 999;
      } else {
        // Handle numeric columns
        const numericColumns = [
          'onHand', 'dailySales', 'actualCost', 'onHandValue', 'reorderPoint', 
          'targetStock', 'systemMin', 'systemMax', 'targetDOI', 'varianceValue', 'backroomQtyNeeded', 'backroomValue', 'class'
        ];

        if (numericColumns.includes(sortConfig.orderBy)) {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        } else {
          // Handle string columns
          aValue = String(aValue || '').toLowerCase();
          bValue = String(bValue || '').toLowerCase();
        }
      }

      if (aValue < bValue) {
        return sortConfig.order === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.order === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const getStatusChip = (status) => {
    const colors = {
      'Out of Stock': 'error',
      'Understocked': 'warning', 
      'Correctly Stocked': 'success',
      'Overstocked': 'info'
    };
    return <Chip label={status} color={colors[status]} size="small" />;
  };

  const uniqueClasses = analysisResults 
    ? [...new Set(analysisResults.map(item => item.class))].filter(c => c).sort()
    : [];

  // Add hierarchy information (Product Line and Department) to analysis results
  const enhancedResults = analysisResults && hierarchyLookup 
    ? analysisResults.map(item => {
        const hierarchy = getHierarchyFromClass(item.class, hierarchyLookup);
        return {
          ...item,
          productLine: hierarchy.productLine,
          department: hierarchy.department
        };
      })
    : analysisResults || [];

  const uniqueProductLines = enhancedResults.length > 0
    ? [...new Set(enhancedResults.map(item => item.productLine))].filter(p => p && p !== 'Unknown').sort()
    : [];

  const uniqueDepartments = enhancedResults.length > 0
    ? [...new Set(enhancedResults.map(item => item.department))].filter(d => d && d !== 'Unknown').sort()
    : [];

  // Calculate filtered results summary (updates with filters) - using Smart categorization
  const filteredSummary = filteredResults.length > 0 ? (() => {
    const outOfStockItems = filteredResults.filter(item => item.status === 'Out of Stock');
    const understockedItems = filteredResults.filter(item => item.status === 'Understocked'); 
    const correctlyStockedItems = filteredResults.filter(item => item.status === 'Correctly Stocked');
    const overstockedItems = filteredResults.filter(item => item.status === 'Overstocked');
    
    // TASK 2: DETAILED DEBUG LOGGING FOR TOTAL VALUE DISCREPANCY
    console.log('\n=== 🔍 TOTAL VALUE DISCREPANCY INVESTIGATION ===');
    
    // Calculate the two totals being compared
    const allSkusTotalValue = summary ? Math.round((summary.outOfStockValue || 0) + (summary.understockedValue || 0) + (summary.correctlyStockedValue || 0) + (summary.overstockedValue || 0)) : 0;
    const filteredTotalValue = filteredResults.reduce((sum, item) => sum + (item.onHandValue || 0), 0);
    const difference = filteredTotalValue - allSkusTotalValue;
    
    console.log(`🔍 ROW 1 (All SKUs) Calculation: $${allSkusTotalValue.toLocaleString()} from ${summary?.totalSkus || 0} items`);
    console.log(`🔍 ROW 4 (Current View) Calculation: $${Math.round(filteredTotalValue).toLocaleString()} from ${filteredResults.length} items`);
    console.log(`🔍 Difference: $${Math.round(difference).toLocaleString()}`);
    
    // Log first 10 items from both datasets for comparison
    console.log('\n--- ALL SKUS DATA SOURCE (from summary) ---');
    if (enhancedResults.length > 0) {
      console.log('First 10 items from enhancedResults:');
      enhancedResults.slice(0, 10).forEach((item, i) => {
        console.log(`${i+1}. SKU: ${item.sku}, OnHand: ${item.onHand}, UnitCost: $${(item.actualCost || 0).toFixed(4)}, OnHandValue: $${(item.onHandValue || 0).toFixed(2)}, Status: ${item.status}`);
      });
    }
    
    console.log('\n--- FILTERED RESULTS DATA SOURCE ---');
    console.log('First 10 items from filteredResults:');
    filteredResults.slice(0, 10).forEach((item, i) => {
      console.log(`${i+1}. SKU: ${item.sku}, OnHand: ${item.onHand}, UnitCost: $${(item.actualCost || 0).toFixed(4)}, OnHandValue: $${(item.onHandValue || 0).toFixed(2)}, Status: ${item.status}`);
    });
    
    // Compare data sources
    const enhancedCount = enhancedResults.length;
    const filteredCount = filteredResults.length;
    console.log(`\n🔍 Data source comparison: enhancedResults=${enhancedCount}, filteredResults=${filteredCount}`);
    
    if (enhancedCount !== filteredCount) {
      console.log(`🔍 Root cause: Different data sources! ${Math.abs(enhancedCount - filteredCount)} items difference`);
    } else {
      console.log('🔍 Same data sources, investigating calculation differences...');
      
      // Compare summary calculation vs direct calculation
      const enhancedDirectTotal = enhancedResults.reduce((sum, item) => sum + (item.onHandValue || 0), 0);
      console.log(`🔍 Enhanced results direct total: $${Math.round(enhancedDirectTotal).toLocaleString()}`);
      console.log(`🔍 Summary component total: $${allSkusTotalValue.toLocaleString()}`);
      
      if (Math.abs(enhancedDirectTotal - allSkusTotalValue) > 1000) {
        console.log('🔍 Root cause: Summary calculation using different method than direct onHandValue sum!');
      }
    }
    
    return {
      totalSkus: filteredResults.length,
      outOfStock: outOfStockItems.length,
      understocked: understockedItems.length,
      correctlyStocked: correctlyStockedItems.length,
      overstocked: overstockedItems.length,
      // Calculate dollar values
      outOfStockValue: outOfStockItems.reduce((sum, item) => sum + (item.shortageValue || 0), 0),
      understockedValue: understockedItems.reduce((sum, item) => sum + (item.shortageValue || 0), 0),
      correctlyStockedValue: correctlyStockedItems.reduce((sum, item) => sum + (item.onHandValue || 0), 0),
      overstockedValue: overstockedItems.reduce((sum, item) => sum + (item.overageValue || 0), 0),
      totalValue: filteredTotalValue,
      
      // Calculate percentages
      outOfStockPercent: ((outOfStockItems.length / filteredResults.length) * 100).toFixed(1),
      understockedPercent: ((understockedItems.length / filteredResults.length) * 100).toFixed(1),
      correctlyStockedPercent: ((correctlyStockedItems.length / filteredResults.length) * 100).toFixed(1),
      overstockedPercent: ((overstockedItems.length / filteredResults.length) * 100).toFixed(1)
    };
  })() : {
    totalSkus: 0,
    outOfStock: 0,
    understocked: 0,
    correctlyStocked: 0,
    overstocked: 0,
    outOfStockValue: 0,
    understockedValue: 0,
    correctlyStockedValue: 0,
    overstockedValue: 0,
    totalValue: 0,
    outOfStockPercent: '0.0',
    understockedPercent: '0.0',
    correctlyStockedPercent: '0.0',
    overstockedPercent: '0.0'
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h4" gutterBottom>
            Inventory Analysis
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            Analyze stock levels and recommend target inventory
          </Typography>
        </Grid>

        {/* Platform Selection & Run Analysis */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, mb: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Analysis Parameters
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              {/* Warehouse Selection */}
              <Box sx={{ mb: 2, p: 2, bgcolor: warehouseSelected ? '#e8f5e9' : '#f5f5f5', borderRadius: 1, border: warehouseSelected ? '2px solid #4caf50' : '1px solid #e0e0e0' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={warehouseSelected}
                      onChange={handleWarehouseToggle}
                      sx={{
                        color: '#7b1fa2',
                        '&.Mui-checked': { color: '#7b1fa2' }
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label="W004"
                        size="small"
                        sx={{ bgcolor: '#7b1fa2', color: 'white', fontWeight: 600 }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Warehouse (Central Distribution)
                      </Typography>
                    </Box>
                  }
                />
                {warehouseSelected && (
                  <Alert severity="info" sx={{ mt: 1, py: 0 }}>
                    Warehouse analysis shows In Stock vs Out of Stock only (no min/max targets)
                  </Alert>
                )}
              </Box>

              {/* Platform Selection */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  Select Platforms ({selectedPlatforms.length} selected)
                </Typography>
                <Box>
                  <Button size="small" onClick={handleSelectAll} sx={{ mr: 1 }} disabled={warehouseSelected}>
                    Select All Platforms
                  </Button>
                  <Button size="small" onClick={handleDeselectAll}>
                    Deselect All
                  </Button>
                </Box>
              </Box>

              <Grid container spacing={1}>
                {platforms.map(platform => (
                  <Grid item key={platform}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedPlatforms.includes(platform)}
                          onChange={() => handlePlatformToggleWithWarehouseCheck(platform)}
                          disabled={warehouseSelected}
                        />
                      }
                      label={platform}
                      sx={{ opacity: warehouseSelected ? 0.5 : 1 }}
                    />
                  </Grid>
                ))}
              </Grid>

              {warehouseSelected && selectedPlatforms.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Platform selection disabled while warehouse is selected
                </Typography>
              )}
            </Box>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleRunAnalysis}
                  disabled={loading || (!warehouseSelected && selectedPlatforms.length === 0)}
                  sx={{
                    py: 2,
                    bgcolor: warehouseSelected ? '#7b1fa2' : undefined,
                    '&:hover': { bgcolor: warehouseSelected ? '#6a1b9a' : undefined }
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : warehouseSelected ? (
                    'Analyze Warehouse (W004)'
                  ) : (
                    `Analyze ${selectedPlatforms.length} Platform${selectedPlatforms.length !== 1 ? 's' : ''}`
                  )}
                </Button>
              </Grid>
              {analysisResults && (
                <>
                  <Grid item xs={12} md={2}>
                    <Button
                      fullWidth
                      variant="outlined"
                      size="large"
                      onClick={handleExport}
                      sx={{ py: 2 }}
                    >
                      Export Results
                    </Button>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="error"
                      size="large"
                      onClick={handleClearAndReload}
                      sx={{ py: 2 }}
                    >
                      Clear & Reset
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* WAREHOUSE SUMMARY CARDS - Only show for W004 analysis */}
        {summary && summary.isWarehouse && (
          <>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 1, mt: 2, color: '#7b1fa2' }}>
                Warehouse Inventory Summary (W004)
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Simple In Stock vs Out of Stock analysis - no min/max targets for warehouse
              </Typography>
            </Grid>

            <Grid container spacing={3} sx={{ mb: 2 }}>
              <Grid item xs={12} md={4}>
                <Card sx={{ border: '2px solid #7b1fa2' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography color="textSecondary" gutterBottom>
                      Total SKUs
                    </Typography>
                    <Typography variant="h3" sx={{ color: '#7b1fa2' }}>
                      {summary.totalSkus.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      in warehouse
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1, fontWeight: 600 }}>
                      ${Math.round(summary.totalInventoryValue || 0).toLocaleString()} total value
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ bgcolor: '#e8f5e9', border: '1px solid #4caf50' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography color="textSecondary" gutterBottom>
                      In Stock
                    </Typography>
                    <Typography variant="h3" sx={{ color: '#4caf50' }}>
                      {summary.inStockPercent}%
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {summary.inStock?.toLocaleString() || '0'} SKUs
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1, fontWeight: 600, color: '#4caf50' }}>
                      ${Math.round(summary.inStockValue || 0).toLocaleString()} value
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ bgcolor: '#ffebee', border: '1px solid #f44336' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography color="textSecondary" gutterBottom>
                      Out of Stock
                    </Typography>
                    <Typography variant="h3" sx={{ color: '#f44336' }}>
                      {summary.outOfStockPercent}%
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {summary.outOfStock?.toLocaleString() || '0'} SKUs
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1, fontWeight: 600, color: '#f44336' }}>
                      $0 value
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </>
        )}

        {/* System Metrics Row - Only show if system data exists and NOT warehouse */}
        {summary && !summary.isWarehouse && summary.systemSkuCount > 0 && (
          <Grid container spacing={3} sx={{ mb: 2 }}>
            <Grid item xs={12} md={2.4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography color="textSecondary" gutterBottom>
                    System Active SKUs
                  </Typography>
                  <Typography variant="h3" sx={{ color: '#1976d2' }}>
                    {summary.systemSkuCount.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    with system data
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1, fontWeight: 600 }}>
                    ${Math.round(summary.systemTotalInventoryValue || 0).toLocaleString()} total value
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={2.4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography color="textSecondary" gutterBottom>
                    System Out of Stock
                  </Typography>
                  <Typography variant="h3" sx={{ color: '#f44336' }}>
                    {summary.systemSkuCount > 0 ? ((summary.systemOutOfStock / summary.systemSkuCount) * 100).toFixed(1) : '0'}%
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {summary.systemOutOfStock?.toLocaleString() || '0'} SKUs
                  </Typography>
                  <Typography variant="caption" color="error" sx={{ display: 'block', fontWeight: 600 }}>
                    ${Math.round(summary.systemOutOfStockValue || 0).toLocaleString()} shortage
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={2.4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography color="textSecondary" gutterBottom>
                    System Understocked
                  </Typography>
                  <Typography variant="h3" sx={{ color: '#ff9800' }}>
                    {summary.systemSkuCount > 0 ? ((summary.systemUnderstocked / summary.systemSkuCount) * 100).toFixed(1) : '0'}%
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {summary.systemUnderstocked?.toLocaleString() || '0'} SKUs
                  </Typography>
                  <Typography variant="caption" color="warning.main" sx={{ display: 'block', fontWeight: 600 }}>
                    ${Math.round(summary.systemUnderstockedValue || 0).toLocaleString()} shortage
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={2.4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography color="textSecondary" gutterBottom>
                    System Correctly Stocked
                  </Typography>
                  <Typography variant="h3" sx={{ color: '#4caf50' }}>
                    {summary.systemSkuCount > 0 ? ((summary.systemCorrectlyStocked / summary.systemSkuCount) * 100).toFixed(1) : '0'}%
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {summary.systemCorrectlyStocked?.toLocaleString() || '0'} SKUs
                  </Typography>
                  <Typography variant="caption" color="success.main" sx={{ display: 'block', fontWeight: 600 }}>
                    ${Math.round(summary.systemCorrectlyStockedValue || 0).toLocaleString()} value
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={2.4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography color="textSecondary" gutterBottom>
                    System Overstocked
                  </Typography>
                  <Typography variant="h3" sx={{ color: '#2196f3' }}>
                    {summary.systemSkuCount > 0 ? ((summary.systemOverstocked / summary.systemSkuCount) * 100).toFixed(1) : '0'}%
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {summary.systemOverstocked?.toLocaleString() || '0'} SKUs
                  </Typography>
                  <Typography variant="caption" color="info.main" sx={{ display: 'block', fontWeight: 600 }}>
                    ${Math.round(summary.systemOverstockedValue || 0).toLocaleString()} overage
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Smart Logic on System SKUs Only - Comparison Row (NOT for warehouse) */}
        {smartOnSystemSummary && smartOnSystemSummary.skuCount > 0 && !isWarehouseAnalysis && (
          <>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 1, mt: 2 }}>
                Smart Logic (System SKUs Only)
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Smart min/max applied to same {smartOnSystemSummary.skuCount.toLocaleString()} SKUs with system data (for direct comparison with System row above)
              </Typography>
            </Grid>
            
            <Grid container spacing={3} sx={{ mb: 2 }}>
              <Grid item xs={12} md={2.4}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography color="textSecondary" gutterBottom>
                      Smart Active SKUs
                    </Typography>
                    <Typography variant="h3" sx={{ color: '#1976d2' }}>
                      {smartOnSystemSummary.skuCount.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      with system data
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1, fontWeight: 600 }}>
                      ${Math.round(smartOnSystemSummary.totalInventoryValue || 0).toLocaleString()} total value
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={2.4}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography color="textSecondary" gutterBottom>
                      Smart Out of Stock
                    </Typography>
                    <Typography variant="h3" sx={{ color: '#f44336' }}>
                      {smartOnSystemSummary.outOfStockPercent}%
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {smartOnSystemSummary.outOfStock.toLocaleString()} SKUs
                    </Typography>
                    <Typography variant="caption" color="error" sx={{ display: 'block', fontWeight: 600 }}>
                      ${Math.round(smartOnSystemSummary.outOfStockValue || 0).toLocaleString()} shortage
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={2.4}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography color="textSecondary" gutterBottom>
                      Smart Understocked
                    </Typography>
                    <Typography variant="h3" sx={{ color: '#ff9800' }}>
                      {smartOnSystemSummary.understockedPercent}%
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {smartOnSystemSummary.understocked.toLocaleString()} SKUs
                    </Typography>
                    <Typography variant="caption" color="warning.main" sx={{ display: 'block', fontWeight: 600 }}>
                      ${Math.round(smartOnSystemSummary.understockedValue || 0).toLocaleString()} shortage
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={2.4}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography color="textSecondary" gutterBottom>
                      Smart Correctly Stocked
                    </Typography>
                    <Typography variant="h3" sx={{ color: '#4caf50' }}>
                      {smartOnSystemSummary.correctlyStockedPercent}%
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {smartOnSystemSummary.correctlyStocked.toLocaleString()} SKUs
                    </Typography>
                    <Typography variant="caption" color="success.main" sx={{ display: 'block', fontWeight: 600 }}>
                      ${Math.round(smartOnSystemSummary.correctlyStockedValue || 0).toLocaleString()} value
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={2.4}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography color="textSecondary" gutterBottom>
                      Smart Overstocked
                    </Typography>
                    <Typography variant="h3" sx={{ color: '#2196f3' }}>
                      {smartOnSystemSummary.overstockedPercent}%
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {smartOnSystemSummary.overstocked.toLocaleString()} SKUs
                    </Typography>
                    <Typography variant="caption" color="info.main" sx={{ display: 'block', fontWeight: 600 }}>
                      ${Math.round(smartOnSystemSummary.overstockedValue || 0).toLocaleString()} overage
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </>
        )}

        {/* Smart Metrics Row - All SKUs (NOT for warehouse) */}
        {summary && !summary.isWarehouse && (
          <Grid container spacing={3} sx={{ mb: 2 }}>
            <Grid item xs={12} md={2.4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography color="textSecondary" gutterBottom>
                    Active SKUs
                  </Typography>
                  <Typography variant="h3" sx={{ color: '#1976d2' }}>
                    {summary.totalSkus.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {summary.storesAnalyzed} stores
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1, fontWeight: 600 }}>
                    ${Math.round(summary.totalInventoryValue || 0).toLocaleString()} total value
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={2.4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography color="textSecondary" gutterBottom>
                    Smart Out of Stock
                  </Typography>
                  <Typography variant="h3" sx={{ color: '#f44336' }}>
                    {summary.outOfStockPercent}%
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {summary.outOfStock.toLocaleString()} SKUs
                  </Typography>
                  <Typography variant="caption" color="error" sx={{ display: 'block', fontWeight: 600 }}>
                    ${Math.round(summary.outOfStockValue || 0).toLocaleString()} shortage
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={2.4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography color="textSecondary" gutterBottom>
                    Smart Understocked
                  </Typography>
                  <Typography variant="h3" sx={{ color: '#ff9800' }}>
                    {summary.understockedPercent}%
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {summary.understocked.toLocaleString()} SKUs
                  </Typography>
                  <Typography variant="caption" color="warning.main" sx={{ display: 'block', fontWeight: 600 }}>
                    ${Math.round(summary.understockedValue || 0).toLocaleString()} shortage
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={2.4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography color="textSecondary" gutterBottom>
                    Smart Correctly Stocked
                  </Typography>
                  <Typography variant="h3" sx={{ color: '#4caf50' }}>
                    {summary.correctlyStockedPercent}%
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {summary.correctlyStocked.toLocaleString()} SKUs
                  </Typography>
                  <Typography variant="caption" color="success.main" sx={{ display: 'block', fontWeight: 600 }}>
                    ${Math.round(summary.correctlyStockedValue || 0).toLocaleString()} value
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={2.4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography color="textSecondary" gutterBottom>
                    Smart Overstocked
                  </Typography>
                  <Typography variant="h3" sx={{ color: '#2196f3' }}>
                    {summary.overstockedPercent}%
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {summary.overstocked.toLocaleString()} SKUs
                  </Typography>
                  <Typography variant="caption" color="info.main" sx={{ display: 'block', fontWeight: 600 }}>
                    ${Math.round(summary.overstockedValue || 0).toLocaleString()} overage
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Backroom Analysis Section (NOT for warehouse) */}
        {summary && !summary.isWarehouse && summary.storesNeedingBackroom > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3, mt: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                Backroom Requirements by Store
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Shows stores where Smart Max exceeds System Max (floor capacity)
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography color="textSecondary" gutterBottom>
                        Stores Needing Backroom
                      </Typography>
                      <Typography variant="h4" sx={{ color: '#1976d2' }}>
                        {summary.storesNeedingBackroom}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography color="textSecondary" gutterBottom>
                        Total Backroom Units
                      </Typography>
                      <Typography variant="h4" sx={{ color: '#ff9800' }}>
                        {summary.totalBackroomUnits?.toLocaleString() || '0'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography color="textSecondary" gutterBottom>
                        Total Backroom Value
                      </Typography>
                      <Typography variant="h4" sx={{ color: '#4caf50' }}>
                        ${Math.round(summary.totalBackroomValue || 0).toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {summary.backroomByStore && Object.keys(summary.backroomByStore).length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    Store Breakdown
                  </Typography>
                  <TableContainer sx={{ maxHeight: 300 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Store</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>SKUs</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Units</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Value</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(summary.backroomByStore)
                          .sort((a, b) => b[1].totalValue - a[1].totalValue)
                          .slice(0, 20)
                          .map(([store, data]) => (
                            <TableRow key={store}>
                              <TableCell>{store}</TableCell>
                              <TableCell align="right">{data.skuCount}</TableCell>
                              <TableCell align="right">{data.totalUnits.toLocaleString()}</TableCell>
                              <TableCell align="right">${Math.round(data.totalValue).toLocaleString()}</TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Paper>
          </Grid>
        )}

        {/* Filters */}
        {analysisResults && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Filter Results
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small" 
                  onClick={handleClearAllFilters}
                  disabled={filters.status.length === 0 && filters.platform.length === 0 && filters.class.length === 0 && filters.productLine.length === 0 && filters.department.length === 0 && !filters.search && !filters.storeNumber}
                >
                  Clear All Filters
                </Button>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={isWarehouseAnalysis
                      ? ['In Stock', 'Out of Stock']
                      : ['Out of Stock', 'Understocked', 'Correctly Stocked', 'Overstocked']
                    }
                    value={filters.status}
                    onChange={(event, newValue) => setFilters(prev => ({ ...prev, status: newValue }))}
                    getOptionLabel={(option) => isWarehouseAnalysis ? option : `Smart ${option}`}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          key={option}
                          variant="outlined"
                          label={isWarehouseAnalysis ? option : `Smart ${option}`}
                          size="small"
                          color={option === 'Out of Stock' ? 'error' : option === 'In Stock' ? 'success' : option === 'Understocked' ? 'warning' : option === 'Correctly Stocked' ? 'success' : 'info'}
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Status"
                        placeholder="Select statuses..."
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={2}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={isWarehouseAnalysis ? ['W004'] : selectedPlatforms}
                    value={filters.platform}
                    onChange={(event, newValue) => setFilters(prev => ({ ...prev, platform: newValue }))}
                    disabled={isWarehouseAnalysis}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          key={option}
                          variant="outlined"
                          label={option}
                          size="small"
                          color={option === 'W004' ? 'secondary' : 'primary'}
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Platform"
                        placeholder={isWarehouseAnalysis ? 'W004 (Warehouse)' : 'Select platforms...'}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={2}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={uniqueClasses}
                    value={filters.class}
                    onChange={(event, newValue) => setFilters(prev => ({ ...prev, class: newValue }))}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          key={option}
                          variant="outlined"
                          label={option}
                          size="small"
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Class"
                        placeholder="Select classes..."
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={12} md={2}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={uniqueProductLines}
                    value={filters.productLine}
                    onChange={(event, newValue) => setFilters(prev => ({ ...prev, productLine: newValue }))}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          key={option}
                          variant="outlined"
                          label={option}
                          size="small"
                          color="secondary"
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Product Line"
                        placeholder="Select product lines..."
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={2}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={uniqueDepartments}
                    value={filters.department}
                    onChange={(event, newValue) => setFilters(prev => ({ ...prev, department: newValue }))}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          key={option}
                          variant="outlined"
                          label={option}
                          size="small"
                          color="success"
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Department"
                        placeholder="Select departments..."
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Store Number"
                    value={filters.storeNumber}
                    onChange={(e) => setFilters(prev => ({ ...prev, storeNumber: e.target.value }))}
                    placeholder="e.g. 2028"
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search SKU or Description"
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}

        {/* Results Table */}
        {analysisResults && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Analysis Results ({filteredResults.length.toLocaleString()} items)
                </Typography>
              </Box>
              
              {/* Filtered Results Summary Cards */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: isWarehouseAnalysis ? '#7b1fa2' : '#1976d2' }}>
                  {isWarehouseAnalysis ? 'Warehouse Filtered View' : 'Current View Summary'}
                </Typography>

                {isWarehouseAnalysis ? (
                  /* WAREHOUSE: Simplified 3-card layout */
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    {/* Total SKUs */}
                    <Grid item xs={12} md={4}>
                      <Card sx={{ bgcolor: '#f3e5f5', border: '2px solid #7b1fa2', height: '100%' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                            TOTAL SKUS
                          </Typography>
                          <Typography variant="h4" sx={{ color: '#7b1fa2', fontWeight: 600, mt: 0.5 }}>
                            {filteredSummary.totalSkus.toLocaleString()}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#7b1fa2', fontWeight: 500, mt: 1 }}>
                            ${Math.round(filteredSummary.totalValue).toLocaleString()} total value
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* In Stock */}
                    <Grid item xs={6} md={4}>
                      <Card sx={{ bgcolor: '#e8f5e9', border: '1px solid #4caf50', height: '100%' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                            IN STOCK
                          </Typography>
                          <Typography variant="h4" sx={{ color: '#4caf50', fontWeight: 600, mt: 0.5 }}>
                            {filteredResults.filter(item => item.status === 'In Stock').length > 0
                              ? ((filteredResults.filter(item => item.status === 'In Stock').length / filteredResults.length) * 100).toFixed(1)
                              : '0'}%
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {filteredResults.filter(item => item.status === 'In Stock').length.toLocaleString()} SKUs
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#4caf50', fontWeight: 500, mt: 0.5 }}>
                            ${Math.round(filteredResults.filter(item => item.status === 'In Stock').reduce((sum, item) => sum + (item.onHandValue || 0), 0)).toLocaleString()} value
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Out of Stock */}
                    <Grid item xs={6} md={4}>
                      <Card sx={{ bgcolor: '#ffebee', border: '1px solid #f44336', height: '100%' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                            OUT OF STOCK
                          </Typography>
                          <Typography variant="h4" sx={{ color: '#f44336', fontWeight: 600, mt: 0.5 }}>
                            {filteredResults.filter(item => item.status === 'Out of Stock').length > 0
                              ? ((filteredResults.filter(item => item.status === 'Out of Stock').length / filteredResults.length) * 100).toFixed(1)
                              : '0'}%
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {filteredResults.filter(item => item.status === 'Out of Stock').length.toLocaleString()} SKUs
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#f44336', fontWeight: 500, mt: 0.5 }}>
                            $0 value
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                ) : (
                  /* PLATFORM: Full 5-card layout */
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    {/* Total SKUs */}
                    <Grid item xs={6} md={2.4}>
                      <Card sx={{ bgcolor: '#f3e5f5', border: '1px solid #e1bee7', height: '100%' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                            ACTIVE SKUS
                          </Typography>
                          <Typography variant="h4" sx={{ color: '#7b1fa2', fontWeight: 600, mt: 0.5 }}>
                            {filteredSummary.totalSkus.toLocaleString()}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#7b1fa2', fontWeight: 500, mt: 1 }}>
                            ${Math.round(filteredSummary.totalValue).toLocaleString()} total value
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Out of Stock */}
                    <Grid item xs={6} md={2.4}>
                      <Card sx={{ bgcolor: '#ffebee', border: '1px solid #ffcdd2', height: '100%' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                            SMART OUT OF STOCK
                          </Typography>
                          <Typography variant="h4" sx={{ color: '#d32f2f', fontWeight: 600, mt: 0.5 }}>
                            {filteredSummary.outOfStockPercent}%
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {filteredSummary.outOfStock.toLocaleString()} SKUs
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#d32f2f', fontWeight: 500, mt: 0.5 }}>
                            $0 shortage
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Understocked */}
                    <Grid item xs={6} md={2.4}>
                      <Card sx={{ bgcolor: '#fff3e0', border: '1px solid #ffcc02', height: '100%' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                            SMART UNDERSTOCKED
                          </Typography>
                          <Typography variant="h4" sx={{ color: '#f57c00', fontWeight: 600, mt: 0.5 }}>
                            {filteredSummary.understockedPercent}%
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {filteredSummary.understocked.toLocaleString()} SKUs
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#f57c00', fontWeight: 500, mt: 0.5 }}>
                            ${Math.round(filteredSummary.understockedValue).toLocaleString()} shortage
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Correctly Stocked */}
                    <Grid item xs={6} md={2.4}>
                      <Card sx={{ bgcolor: '#e8f5e8', border: '1px solid #c8e6c9', height: '100%' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                            SMART CORRECTLY STOCKED
                          </Typography>
                          <Typography variant="h4" sx={{ color: '#388e3c', fontWeight: 600, mt: 0.5 }}>
                            {filteredSummary.correctlyStockedPercent}%
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {filteredSummary.correctlyStocked.toLocaleString()} SKUs
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#388e3c', fontWeight: 500, mt: 0.5 }}>
                            ${Math.round(filteredSummary.correctlyStockedValue).toLocaleString()} value
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Overstocked */}
                    <Grid item xs={6} md={2.4}>
                      <Card sx={{ bgcolor: '#e1f5fe', border: '1px solid #b3e5fc', height: '100%' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                            SMART OVERSTOCKED
                          </Typography>
                          <Typography variant="h4" sx={{ color: '#0277bd', fontWeight: 600, mt: 0.5 }}>
                            {filteredSummary.overstockedPercent}%
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {filteredSummary.overstocked.toLocaleString()} SKUs
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#0277bd', fontWeight: 500, mt: 0.5 }}>
                            ${Math.round(filteredSummary.overstockedValue).toLocaleString()} excess
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                )}
              </Box>
              
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table 
                  stickyHeader 
                  size="small"
                  key={`table-${filters.status}-${filters.platform}-${filters.storeNumber}-${filters.class}-${filters.productLine}-${filters.department}-${filters.search}-${Date.now()}`}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <TableSortLabel
                          active={sortConfig.orderBy === 'analysisplatform'}
                          direction={sortConfig.orderBy === 'analysisplatform' ? sortConfig.order : 'asc'}
                          onClick={() => handleRequestSort('analysisplatform')}
                        >
                          Platform
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <TableSortLabel
                          active={sortConfig.orderBy === 'site'}
                          direction={sortConfig.orderBy === 'site' ? sortConfig.order : 'asc'}
                          onClick={() => handleRequestSort('site')}
                        >
                          Store/Site
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <TableSortLabel
                          active={sortConfig.orderBy === 'sku'}
                          direction={sortConfig.orderBy === 'sku' ? sortConfig.order : 'asc'}
                          onClick={() => handleRequestSort('sku')}
                        >
                          SKU
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <TableSortLabel
                          active={sortConfig.orderBy === 'description'}
                          direction={sortConfig.orderBy === 'description' ? sortConfig.order : 'asc'}
                          onClick={() => handleRequestSort('description')}
                        >
                          Description
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <TableSortLabel
                          active={sortConfig.orderBy === 'productLine'}
                          direction={sortConfig.orderBy === 'productLine' ? sortConfig.order : 'asc'}
                          onClick={() => handleRequestSort('productLine')}
                        >
                          Product Line
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <TableSortLabel
                          active={sortConfig.orderBy === 'department'}
                          direction={sortConfig.orderBy === 'department' ? sortConfig.order : 'asc'}
                          onClick={() => handleRequestSort('department')}
                        >
                          Department
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <TableSortLabel
                          active={sortConfig.orderBy === 'class'}
                          direction={sortConfig.orderBy === 'class' ? sortConfig.order : 'asc'}
                          onClick={() => handleRequestSort('class')}
                        >
                          Class
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        <TableSortLabel
                          active={sortConfig.orderBy === 'onHand'}
                          direction={sortConfig.orderBy === 'onHand' ? sortConfig.order : 'asc'}
                          onClick={() => handleRequestSort('onHand')}
                        >
                          On Hand
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        <TableSortLabel
                          active={sortConfig.orderBy === 'dailySales'}
                          direction={sortConfig.orderBy === 'dailySales' ? sortConfig.order : 'asc'}
                          onClick={() => handleRequestSort('dailySales')}
                        >
                          Daily Sales
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <TableSortLabel
                          active={sortConfig.orderBy === 'inventoryModel'}
                          direction={sortConfig.orderBy === 'inventoryModel' ? sortConfig.order : 'asc'}
                          onClick={() => handleRequestSort('inventoryModel')}
                        >
                          Inventory Model
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        <TableSortLabel
                          active={sortConfig.orderBy === 'actualCost'}
                          direction={sortConfig.orderBy === 'actualCost' ? sortConfig.order : 'asc'}
                          onClick={() => handleRequestSort('actualCost')}
                        >
                          <Tooltip 
                            title="Actual inventory cost per unit (OH $ ÷ OH Units). Represents what was paid for the inventory, not retail price."
                            arrow
                          >
                            <span style={{ textDecoration: 'underline dotted', cursor: 'help' }}>
                              Unit Cost
                            </span>
                          </Tooltip>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        <TableSortLabel
                          active={sortConfig.orderBy === 'onHandValue'}
                          direction={sortConfig.orderBy === 'onHandValue' ? sortConfig.order : 'asc'}
                          onClick={() => handleRequestSort('onHandValue')}
                        >
                          On Hand Value
                        </TableSortLabel>
                      </TableCell>
                      {/* Status Column - always shown but label changes for warehouse */}
                      <TableCell sx={{ fontWeight: 600 }}>
                        <TableSortLabel
                          active={sortConfig.orderBy === 'status'}
                          direction={sortConfig.orderBy === 'status' ? sortConfig.order : 'asc'}
                          onClick={() => handleRequestSort('status')}
                        >
                          {isWarehouseAnalysis ? 'Status' : 'Smart Status'}
                        </TableSortLabel>
                      </TableCell>
                      {/* Hide Smart/System columns for warehouse */}
                      {!isWarehouseAnalysis && (
                        <>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            <TableSortLabel
                              active={sortConfig.orderBy === 'reorderPoint'}
                              direction={sortConfig.orderBy === 'reorderPoint' ? sortConfig.order : 'asc'}
                              onClick={() => handleRequestSort('reorderPoint')}
                            >
                              <Tooltip
                                title="Smart Min = Daily Sales × (Gap Equivalent + Highest Day Factor). Uses airport traffic patterns to ensure adequate inventory for delivery gaps AND peak traffic days."
                                arrow
                              >
                                <span style={{ textDecoration: 'underline dotted', cursor: 'help' }}>
                                  Smart Reorder Point
                                </span>
                              </Tooltip>
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            <TableSortLabel
                              active={sortConfig.orderBy === 'targetStock'}
                              direction={sortConfig.orderBy === 'targetStock' ? sortConfig.order : 'asc'}
                              onClick={() => handleRequestSort('targetStock')}
                            >
                              Smart Target Stock
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            <TableSortLabel
                              active={sortConfig.orderBy === 'systemMin'}
                              direction={sortConfig.orderBy === 'systemMin' ? sortConfig.order : 'asc'}
                              onClick={() => handleRequestSort('systemMin')}
                            >
                              System Min
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            <TableSortLabel
                              active={sortConfig.orderBy === 'systemMax'}
                              direction={sortConfig.orderBy === 'systemMax' ? sortConfig.order : 'asc'}
                              onClick={() => handleRequestSort('systemMax')}
                            >
                              System Max
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            <TableSortLabel
                              active={sortConfig.orderBy === 'targetDOI'}
                              direction={sortConfig.orderBy === 'targetDOI' ? sortConfig.order : 'asc'}
                              onClick={() => handleRequestSort('targetDOI')}
                            >
                              Target DOI
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            <TableSortLabel
                              active={sortConfig.orderBy === 'systemStatus'}
                              direction={sortConfig.orderBy === 'systemStatus' ? sortConfig.order : 'asc'}
                              onClick={() => handleRequestSort('systemStatus')}
                            >
                              System Status
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            <TableSortLabel
                              active={sortConfig.orderBy === 'backroomQtyNeeded'}
                              direction={sortConfig.orderBy === 'backroomQtyNeeded' ? sortConfig.order : 'asc'}
                              onClick={() => handleRequestSort('backroomQtyNeeded')}
                            >
                              Backroom Qty
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            <TableSortLabel
                              active={sortConfig.orderBy === 'backroomValue'}
                              direction={sortConfig.orderBy === 'backroomValue' ? sortConfig.order : 'asc'}
                              onClick={() => handleRequestSort('backroomValue')}
                            >
                              Backroom Value
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            <TableSortLabel
                              active={sortConfig.orderBy === 'varianceValue'}
                              direction={sortConfig.orderBy === 'varianceValue' ? sortConfig.order : 'asc'}
                              onClick={() => handleRequestSort('varianceValue')}
                            >
                              <Tooltip
                                title="Dollar variance from Smart Target Stock. Formula: (On Hand - Smart Target) × Unit Cost. Positive = overstocked (excess inventory), Negative = understocked (shortage), Zero = optimal level."
                                arrow
                              >
                                <span style={{ textDecoration: 'underline dotted', cursor: 'help' }}>
                                  Variance ($)
                                </span>
                              </Tooltip>
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            <TableSortLabel
                              active={sortConfig.orderBy === 'tier'}
                              direction={sortConfig.orderBy === 'tier' ? sortConfig.order : 'asc'}
                              onClick={() => handleRequestSort('tier')}
                            >
                              Tier
                            </TableSortLabel>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      const sortedResults = sortData(filteredResults);
                      const displayItems = sortedResults.slice(0, 1000);
                      console.log('=== TABLE RENDERING DEBUG ===');
                      console.log('filteredResults.length:', filteredResults.length);
                      console.log('displayItems.length:', displayItems.length);
                      console.log('First 3 display items:');
                      displayItems.slice(0, 3).forEach((item, i) => {
                        console.log(`Display ${i}: SKU ${item.sku}, Status "${item.status}", Platform ${item.analysisplatform}`);
                      });
                      return displayItems;
                    })().map((item, index) => (
                      <TableRow key={`${item.analysisplatform}-${item.site}-${item.sku}` || index}>
                        <TableCell>
                          <Chip label={item.analysisplatform} size="small" color="primary" />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          {item.site}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{item.sku}</TableCell>
                        <TableCell>{item.description}</TableCell>
                        <TableCell sx={{ fontSize: '0.85rem' }}>{item.productLine}</TableCell>
                        <TableCell sx={{ fontSize: '0.85rem' }}>{item.department}</TableCell>
                        <TableCell>{item.class}</TableCell>
                        <TableCell align="right">{item.onHand.toLocaleString()}</TableCell>
                        <TableCell align="right">{item.dailySales.toFixed(1)}</TableCell>
                        <TableCell>
                          {(() => {
                            if (index < 3) {
                              console.log(`Frontend DEBUG: Row ${index}, SKU ${item.sku}, inventoryModel: "${item.inventoryModel}"`);
                            }
                            return (
                              <Chip 
                                label={item.inventoryModel || 'N/A'} 
                                size="small" 
                                variant="outlined"
                                color={item.inventoryModel === '4x' ? 'error' : item.inventoryModel === '3x' ? 'warning' : 'default'}
                              />
                            );
                          })()}
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              ${item.actualCost?.toFixed(4) || '0.0000'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                              AUR: ${item.oldAur?.toFixed(2) || '0.00'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">${item.onHandValue?.toLocaleString() || '0'}</TableCell>
                        {/* Status - always shown but simplified for warehouse */}
                        <TableCell>
                          {isWarehouseAnalysis ? (
                            <Chip
                              label={item.status}
                              size="small"
                              color={item.status === 'In Stock' ? 'success' : 'error'}
                            />
                          ) : (
                            getStatusChip(item.status)
                          )}
                        </TableCell>
                        {/* Hide Smart/System columns for warehouse */}
                        {!isWarehouseAnalysis && (
                          <>
                            <TableCell align="right">{item.reorderPoint?.toLocaleString() || '0'}</TableCell>
                            <TableCell align="right">{item.targetStock?.toLocaleString() || '0'}</TableCell>
                            <TableCell align="right">
                              {item.systemMin ? item.systemMin.toLocaleString() : 'N/A'}
                            </TableCell>
                            <TableCell align="right">
                              {item.systemMax ? item.systemMax.toLocaleString() : 'N/A'}
                            </TableCell>
                            <TableCell align="right">
                              {item.targetDOI ? `${item.targetDOI}` : 'N/A'}
                            </TableCell>
                            <TableCell>
                              {item.hasSystemData ? (
                                <Chip
                                  label={item.systemStatus.replace('System ', '')}
                                  size="small"
                                  color={
                                    item.systemStatus.includes('Out') ? 'error' :
                                    item.systemStatus.includes('Under') ? 'warning' :
                                    item.systemStatus.includes('Correct') ? 'success' : 'info'
                                  }
                                />
                              ) : (
                                <Chip label="No System Data" size="small" color="default" />
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {item.backroomQtyNeeded > 0 ? item.backroomQtyNeeded.toLocaleString() : '0'}
                            </TableCell>
                            <TableCell align="right">
                              ${item.backroomValue?.toLocaleString() || '0'}
                            </TableCell>
                            <TableCell align="right" sx={{
                              color: item.varianceValue > 0 ? '#2196f3' : item.varianceValue < 0 ? '#f44336' : '#4caf50'
                            }}>
                              {item.varianceValue > 0 ? '+' : ''}${item.varianceValue?.toLocaleString() || '0'}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={item.tier}
                                size="small"
                                variant="outlined"
                                color={item.tier === 'Top 10%' ? 'success' : item.tier === 'Top 30%' ? 'warning' : 'default'}
                              />
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {filteredResults.length > 1000 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Showing first 1,000 results of {filteredResults.length.toLocaleString()} total items.
                  Use filters to narrow down results.
                </Alert>
              )}
            </Paper>
          </Grid>
        )}

        {!analysisResults && !loading && (
          <Grid item xs={12}>
            <Paper sx={{ p: 6, textAlign: 'center' }}>
              <Typography variant="h6" color="textSecondary" gutterBottom>
                Ready to Analyze Inventory
              </Typography>
              <Typography variant="body1" color="textSecondary">
                Select a platform above and click "Run Analysis" to begin inventory optimization analysis.
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Footer */}
      <Box
        sx={{
          mt: 6,
          pt: 2,
          pb: 2,
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 1
        }}
      >
        <Typography variant="caption" color="textSecondary">
          Powered by
        </Typography>
        <img
          src={ClarisLogo}
          alt="Claris AI"
          style={{ height: 40, opacity: 0.7 }}
        />
      </Box>
    </Container>
  );
};

export default InventoryAnalysis;